import React from 'react';
import { motion } from 'framer-motion';
import { SiMeta, SiGooglegemini, SiClaude } from 'react-icons/si';

/**
 * Teknologi yang dipakai Nerava. Bukan klaim kemitraan resmi/sponsorship —
 * ini murni daftar API pihak ketiga yang diintegrasikan produk.
 */
export const LogoMarquee = ({ className = '' }) => {
  const partners = [
    {
      name: 'Meta',
      description: 'WhatsApp Cloud API, Instagram & Messenger — API resmi Meta',
      logo: <SiMeta className="w-9 h-9 sm:w-10 sm:h-10" style={{ color: '#0081FB' }} />,
      accentColor: '#0081FB',
      bgGradient: 'from-blue-500/10 to-purple-500/10',
      borderColor: 'border-blue-200 dark:border-blue-800',
      hoverBorder: 'group-hover:border-blue-400 dark:group-hover:border-blue-500',
      glowColor: 'group-hover:shadow-blue-500/20',
    },
    {
      name: 'Claude',
      description: 'Model AI utama yang menjawab chat & membaca foto pelanggan',
      logo: <SiClaude className="w-9 h-9 sm:w-10 sm:h-10" style={{ color: '#D97757' }} />,
      accentColor: '#D97757',
      bgGradient: 'from-amber-500/10 to-rose-500/10',
      borderColor: 'border-amber-200 dark:border-amber-800',
      hoverBorder: 'group-hover:border-amber-400 dark:group-hover:border-amber-500',
      glowColor: 'group-hover:shadow-amber-500/20',
    },
    {
      name: 'Gemini',
      description: 'Model AI cadangan, menjawab saat model utama sibuk',
      logo: <SiGooglegemini className="w-9 h-9 sm:w-10 sm:h-10" style={{ color: '#8E75B2' }} />,
      accentColor: '#8E75B2',
      bgGradient: 'from-blue-500/10 via-purple-500/10 to-rose-500/10',
      borderColor: 'border-purple-200 dark:border-purple-800',
      hoverBorder: 'group-hover:border-purple-400 dark:group-hover:border-purple-500',
      glowColor: 'group-hover:shadow-purple-500/20',
    },
    {
      name: 'Komerce',
      description: 'Data ongkir & layanan kurir Indonesia',
      logo: (
        <img 
          src="https://is1-ssl.mzstatic.com/image/thumb/Purple125/v4/49/0a/a0/490aa0f7-e065-8f62-6a61-36573dcea2a8/AppIcon-0-0-1x_U007emarketing-0-0-0-10-0-0-sRGB-0-0-0-GLES2_U002c0-512MB-85-220-0-0.png/200x200bb.jpg"
          alt="Komerce"
          className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg"
        />
      ),
      accentColor: '#F97316',
      bgGradient: 'from-orange-500/10 to-red-500/10',
      borderColor: 'border-orange-200 dark:border-orange-800',
      hoverBorder: 'group-hover:border-orange-400 dark:group-hover:border-orange-500',
      glowColor: 'group-hover:shadow-orange-500/20',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15, delayChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.95 },
    visible: { 
      opacity: 1, y: 0, scale: 1,
      transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] }
    }
  };

  return (
    <div className={`${className}`}>
      {/* Header */}
      <motion.div 
        className="mb-10 sm:mb-12 flex flex-col items-center text-center"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
      >
        <div className="inline-flex items-center gap-2.5 mb-4">
          <span className="h-px w-8 bg-indigo-500" />
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-indigo-600 dark:text-indigo-400">
            <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
            Teknologi yang Kami Pakai
          </span>
          <span className="h-px w-8 bg-indigo-500" />
        </div>
        <h3 className="text-lg sm:text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50" style={{ fontFamily: 'Manrope' }}>
          Dibangun di atas API resmi
        </h3>
      </motion.div>

      {/* Partner Cards */}
      <motion.div
        className="max-w-5xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        {partners.map((partner) => (
          <motion.div
            key={partner.name}
            data-testid={`tech-card-${partner.name.toLowerCase()}`}
            variants={itemVariants}
            className={`
              group relative overflow-hidden
              bg-gradient-to-br ${partner.bgGradient}
              rounded-2xl border ${partner.borderColor} ${partner.hoverBorder}
              p-5 sm:p-6
              transition-all duration-300 cursor-default
              hover:shadow-xl ${partner.glowColor}
            `}
            whileHover={{ y: -6, transition: { duration: 0.25 } }}
          >
            {/* Decorative glow */}
            <div 
              className="absolute -top-12 -right-12 w-32 h-32 rounded-full blur-3xl opacity-0 group-hover:opacity-30 transition-opacity duration-500"
              style={{ backgroundColor: partner.accentColor }}
            />
            
            {/* Content */}
            <div className="relative z-10">
              <motion.div 
                className="mb-4"
                whileHover={{ scale: 1.1, rotate: 3 }}
                transition={{ type: 'spring', stiffness: 300 }}
              >
                {partner.logo}
              </motion.div>
              
              <h4 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-zinc-50 mb-1" style={{ fontFamily: 'Manrope' }}>
                {partner.name}
              </h4>
              <p className="text-sm text-slate-500 dark:text-zinc-400 leading-relaxed">
                {partner.description}
              </p>
            </div>

            {/* Bottom accent line */}
            <div 
              className="absolute bottom-0 left-0 right-0 h-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              style={{ background: `linear-gradient(90deg, transparent, ${partner.accentColor}, transparent)` }}
            />
          </motion.div>
        ))}
      </motion.div>
    </div>
  );
};

export default LogoMarquee;
